/**
 * Coach Mode Authentication Modal
 * ================================
 * Handles biometric (WebAuthn) authentication with PIN fallback for Coach Mode elevation.
 * 
 * Flow:
 * 1. Check if WebAuthn is available
 * 2. If available, attempt biometric auth (Face ID / Touch ID)
 * 3. If biometric fails or unavailable, show PIN entry
 * 4. On successful auth, activate coach mode
 */

import React, { useState, useEffect, useRef } from 'react';
import { Shield, Fingerprint, Lock, X, AlertCircle, CheckCircle } from 'lucide-react';

// Default PIN for coach mode
const DEFAULT_COACH_PIN = '1234';

const CoachModeAuthModal = ({ isOpen, onClose, onSuccess, workoutId, verifyPin: externalVerifyPin, activateCoachMode: externalActivate }) => {
  // Use external props if provided, otherwise use defaults
  const verifyPin = externalVerifyPin || ((pin) => pin === DEFAULT_COACH_PIN);
  const activateCoachMode = externalActivate || (() => {});
  
  const [authMethod, setAuthMethod] = useState('checking'); // 'checking' | 'biometric' | 'pin'
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const pinInputRef = useRef(null);

  // Check WebAuthn support on mount
  useEffect(() => {
    if (!isOpen) return;
    
    const checkBiometricSupport = async () => {
      try {
        // Check if WebAuthn is available
        if (window.PublicKeyCredential) {
          // Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricSupported(available);
          setAuthMethod(available ? 'biometric' : 'pin');
          
          if (available) {
            // Auto-trigger biometric auth
            attemptBiometricAuth();
          }
        } else {
          setBiometricSupported(false);
          setAuthMethod('pin');
        }
      } catch (err) {
        console.log('[CoachMode] Biometric check failed:', err);
        setBiometricSupported(false);
        setAuthMethod('pin');
      }
    };
    
    checkBiometricSupport();
  }, [isOpen]);

  // Focus PIN input when switching to PIN method
  useEffect(() => {
    if (authMethod === 'pin' && pinInputRef.current) {
      setTimeout(() => pinInputRef.current?.focus(), 100);
    }
  }, [authMethod]);

  const attemptBiometricAuth = async () => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      // Create a simple WebAuthn challenge for local verification
      // Note: In production, this challenge should come from the server
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);
      
      const publicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: "Gym Management App",
          id: window.location.hostname
        },
        user: {
          id: new Uint8Array(16),
          name: "coach@gym.local",
          displayName: "Coach Mode"
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },   // ES256
          { alg: -257, type: "public-key" }  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required"
        },
        timeout: 60000,
        attestation: "none"
      };

      // Try to use existing credential or create new one
      try {
        // First, try to authenticate with existing credential
        const getOptions = {
          challenge,
          timeout: 60000,
          userVerification: "required",
          rpId: window.location.hostname
        };
        
        await navigator.credentials.get({ publicKey: getOptions });
        handleAuthSuccess();
      } catch (getErr) {
        // If no existing credential, create one
        if (getErr.name === 'NotAllowedError' || getErr.name === 'InvalidStateError') {
          // User cancelled or no credentials - fall back to PIN
          setAuthMethod('pin');
          setIsAuthenticating(false);
          return;
        }
        
        // Try creating new credential
        try {
          await navigator.credentials.create({ publicKey: publicKeyCredentialCreationOptions });
          handleAuthSuccess();
        } catch (createErr) {
          console.log('[CoachMode] Biometric create failed:', createErr);
          setAuthMethod('pin');
          setError('Autenticación biométrica no disponible. Use el PIN.');
        }
      }
    } catch (err) {
      console.log('[CoachMode] Biometric auth failed:', err);
      setAuthMethod('pin');
      setError('Autenticación biométrica fallida. Use el PIN.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handlePinSubmit = (e) => {
    e?.preventDefault();
    setError(null);
    
    if (pin.length !== 4) {
      setError('El PIN debe tener 4 dígitos');
      return;
    }
    
    if (verifyPin(pin)) {
      handleAuthSuccess();
    } else {
      setError('PIN incorrecto');
      setPin('');
      pinInputRef.current?.focus();
    }
  };

  const handleAuthSuccess = () => {
    activateCoachMode(workoutId);
    onSuccess();
    onClose();
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
    
    // Auto-submit when 4 digits entered
    if (value.length === 4) {
      setTimeout(() => {
        if (verifyPin(value)) {
          handleAuthSuccess();
        } else {
          setError('PIN incorrecto');
          setPin('');
          pinInputRef.current?.focus();
        }
      }, 100);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-start sm:items-center justify-center p-4 overflow-y-auto z-[99999]"
      data-testid="coach-mode-auth-modal"
    >
      <div className="glass-card p-8 max-w-md w-full relative my-4 sm:my-8 max-h-[90dvh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-blue-300 hover:text-white transition-colors"
          data-testid="coach-auth-close-btn"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-600/30 mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Activar Modo Entrenador
          </h2>
          <p className="text-blue-200 text-sm">
            Autenticación requerida para acceder a los controles de estructura del entrenamiento
          </p>
        </div>

        {/* Auth Method: Checking */}
        {authMethod === 'checking' && (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-blue-200">Verificando opciones de autenticación...</p>
          </div>
        )}

        {/* Auth Method: Biometric */}
        {authMethod === 'biometric' && (
          <div className="text-center py-4">
            <button
              onClick={attemptBiometricAuth}
              disabled={isAuthenticating}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="biometric-auth-btn"
            >
              {isAuthenticating ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Autenticando...
                </>
              ) : (
                <>
                  <Fingerprint className="w-6 h-6" />
                  Usar Face ID / Touch ID
                </>
              )}
            </button>
            
            <div className="mt-4 pt-4 border-t border-blue-800">
              <button
                onClick={() => setAuthMethod('pin')}
                className="text-blue-300 hover:text-blue-200 text-sm underline"
                data-testid="switch-to-pin-btn"
              >
                Usar PIN en su lugar
              </button>
            </div>
          </div>
        )}

        {/* Auth Method: PIN */}
        {authMethod === 'pin' && (
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <Lock className="w-12 h-12 text-blue-400 mx-auto mb-2" />
              <p className="text-blue-200 text-sm">
                Introduce el PIN de entrenador
              </p>
            </div>
            
            <div className="relative">
              <input
                ref={pinInputRef}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pin}
                onChange={handlePinChange}
                className="w-full text-center text-3xl tracking-[1em] py-4 px-6 bg-blue-900/50 border-2 border-blue-600 rounded-lg text-white focus:outline-none focus:border-blue-400 transition-colors"
                placeholder="••••"
                autoComplete="off"
                data-testid="pin-input"
              />
              <div className="flex justify-center gap-2 mt-3">
                {[0, 1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all ${
                      pin.length > i ? 'bg-blue-400' : 'bg-blue-800'
                    }`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={pin.length !== 4}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:opacity-50 text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
              data-testid="pin-submit-btn"
            >
              <CheckCircle className="w-5 h-5" />
              Activar Modo Entrenador
            </button>

            {biometricSupported && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setAuthMethod('biometric');
                    attemptBiometricAuth();
                  }}
                  className="text-blue-300 hover:text-blue-200 text-sm underline"
                >
                  Usar autenticación biométrica
                </button>
              </div>
            )}
          </form>
        )}

        {/* Info Footer */}
        <div className="mt-6 pt-4 border-t border-blue-800">
          <p className="text-blue-400 text-xs text-center">
            El Modo Entrenador permite modificar la estructura del entrenamiento.
            Se desactivará automáticamente al salir de la sesión.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoachModeAuthModal;
